<template>
  <div class="funeraria-dashboard">
    <div class="header">
      <h2>Dashboard Funeraria</h2>
      <el-button @click="userStore.logout()" type="primary">Logout</el-button>
    </div>

    <el-tabs v-model="activeTab">
      <!-- Vagas Disponíveis Tab -->
      <el-tab-pane label="Vagas Disponíveis" name="vagasDisponiveis">
        <div class="tab-content">
          <h3>Vagas Disponíveis</h3>
          <el-table :data="vagasDisponiveis" v-loading="loadingVagasDisponiveis" style="margin-top: 20px; width: 100%">
            <el-table-column prop="quadra" label="Quadra" />
            <el-table-column prop="numeroSepultura" label="Nº Sepultura" />
            <el-table-column label="Tipo">
              <template #default="{ row }">
                <span>
                  {{ row.tipo || 'N/A' }}
                  <template v-if="row.obeso">
                    (A:{{ row.altura }} L:{{ row.largura }})
                  </template>
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Cemitério">
              <template #default="{ row }">
                {{ row.cemiterio ? row.cemiterio.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Ações" width="180">
              <template #default="{ row }">
                <el-button size="small" @click="selectVaga(row)">Selecionar</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <!-- Criar Requisição Tab -->
      <el-tab-pane label="Criar Requisição" name="criarRequisicao">
        <div class="tab-content">
          <h3>Criar Requisição</h3>
          <el-form :model="newRequisicao" :rules="requisicaoRules" ref="requisicaoFormRef" label-position="top">
            <el-form-item label="Nome do Falecido" prop="nomeFalecido">
              <el-input v-model="newRequisicao.nomeFalecido" />
            </el-form-item>
            <el-form-item label="CPF do Falecido" prop="cpfFalecido">
              <el-input v-model="newRequisicao.cpfFalecido" @input="applyCpfMask" />
            </el-form-item>
            <el-form-item label="Data de Falecimento" prop="dataFalecimento">
              <el-date-picker
                  v-model="newRequisicao.dataFalecimento"
                  type="date"
                  placeholder="Selecione a data"
              />
            </el-form-item>
            <el-form-item label="Vaga Selecionada" prop="vagaId">
              <el-input
                  v-model="newRequisicao.vagaId"
                  placeholder="Escolha uma vaga na aba 'Vagas Disponíveis'"
                  disabled
              />
            </el-form-item>
            <!-- Data e Hora da Requisição -->
            <el-form-item label="Data da Requisição" prop="dataRequisicao">
              <el-date-picker
                  v-model="newRequisicao.dataRequisicao"
                  type="date"
                  placeholder="Selecione a data"
                  :disabled-date="disabledRequisicaoDate"
                  @change="onDateChange"
              />
            </el-form-item>
            <el-form-item label="Hora da Requisição" prop="horaRequisicao">
              <el-select
                  v-model="newRequisicao.horaRequisicao"
                  placeholder="Selecione o horário"
                  :disabled="!newRequisicao.dataRequisicao"
                  v-loading="loadingTimeSlots"
              >
                <el-option
                    v-for="item in availableTimeSlots"
                    :key="item.value"
                    :label="item.value"
                    :value="item.value"
                    :disabled="item.disabled"
                />
              </el-select>
            </el-form-item>
            <!-- Upload de Arquivos com indicadores de status -->
            <el-form-item label="Arquivos (Enviar RG e DO)">
              <div class="upload-container">
                <el-upload
                    ref="rgUploadRef"
                    action=""
                    :show-file-list="false"
                    :limit="1"
                    :auto-upload="false"
                    accept="application/pdf"
                    :before-upload="beforeUpload"
                    @change="onRGFileChange"
                    @remove="onRGFileRemove"
                >
                  <el-button type="primary">Adicionar RG</el-button>
                </el-upload>
                <span class="file-status">
                  {{ rgFileName }}
                  <el-icon @click="onRGFileRemove" style="cursor: pointer; margin-left: 4px;">
                    <Close />
                  </el-icon>
                </span>
                <el-upload
                    ref="doUploadRef"
                    action=""
                    :show-file-list="false"
                    :limit="1"
                    :auto-upload="false"
                    accept="application/pdf"
                    :before-upload="beforeUpload"
                    @change="onDOFileChange"
                    @remove="onDOFileRemove"
                >
                  <el-button type="primary">Adicionar DO</el-button>
                </el-upload>
                <span class="file-status">
                  {{ doFileName }}
                  <el-icon @click="onDOFileRemove" style="cursor: pointer; margin-left: 4px;">
                    <Close />
                  </el-icon>
                </span>
              </div>
              <template #tip>
                <div class="el-upload-tip">Apenas arquivos PDF com tamanho máximo de 5MB.</div>
              </template>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="confirmCreateRequisicao">
                Criar Requisição
              </el-button>
            </el-form-item>
          </el-form>
        </div>
      </el-tab-pane>

      <!-- Minhas Requisições Tab -->
      <el-tab-pane label="Minhas Requisições" name="minhasRequisicoes">
        <div class="tab-content">
          <h3>Minhas Requisições</h3>
          <el-table :data="minhasRequisicoes" v-loading="loadingMinhasRequisicoes" style="margin-top: 20px; width: 100%">
            <el-table-column prop="nomeFalecido" label="Falecido" />
            <el-table-column label="Obeso?" width="90">
              <template #default="{ row }">{{ row.obeso ? 'Sim' : 'Não' }}</template>
            </el-table-column>
            <el-table-column label="Status" :formatter="formatStatus" />
            <el-table-column label="Vaga (Quadra - Sepultura - Tipo)" :formatter="formatVaga" />
            <el-table-column label="Cemitério">
              <template #default="{ row }">
                {{ row.vaga && row.vaga.cemiterio ? row.vaga.cemiterio.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Funeraria">
              <template #default="{ row }">
                {{ row.funeraria ? row.funeraria.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column prop="criadoEm" label="Criado Em" :formatter="formatCriadoEm"/>
            <!-- Novas colunas: Data/Hora da Requisição e Reason -->
            <el-table-column prop="dataRequisicao" label="Data da Requisição" />
            <el-table-column prop="horaRequisicao" label="Hora da Requisição" />
            <el-table-column prop="reason" label="Reason" />
          </el-table>
        </div>
      </el-tab-pane>

      <!-- Requisições em andamento Tab -->
      <el-tab-pane label="Requisições em andamento" name="requisicoesAndamento">
        <div class="tab-content">
          <h3>Requisições em andamento</h3>
          <el-table :data="requisicoesAndamento" v-loading="loadingRequisicoesAndamento" style="margin-top: 20px; width: 100%">
            <el-table-column prop="nomeFalecido" label="Falecido" />
            <el-table-column label="Obeso?" width="90">
              <template #default="{ row }">{{ row.obeso ? 'Sim' : 'Não' }}</template>
            </el-table-column>
            <el-table-column prop="status" label="Status" />
            <el-table-column label="Vaga (Quadra - Sepultura)" :formatter="formatVaga" />
            <el-table-column label="Cemitério">
              <template #default="{ row }">
                {{ row.cemiterio ? row.cemiterio.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column label="Funeraria">
              <template #default="{ row }">
                {{ row.funeraria ? row.funeraria.nome : 'N/A' }}
              </template>
            </el-table-column>
            <el-table-column prop="criadoEm" label="Criado Em" />
          </el-table>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, onUnmounted } from 'vue';
import apiClient from '@/plugins/axios';
import { ElMessage } from 'element-plus';
import { useUserStore } from '@/stores/user';
import { Close } from '@element-plus/icons-vue';

const userStore = useUserStore();
const activeTab = ref('vagasDisponiveis');

// Vagas Disponíveis
const vagasDisponiveis = ref([]);
const loadingVagasDisponiveis = ref(false);
const fetchVagasDisponiveis = async () => {
  loadingVagasDisponiveis.value = true;
  try {
    const { data } = await apiClient.get('/vagas/funeraria/disponiveis');
    vagasDisponiveis.value = data;
  } catch (error) {
    console.error('Erro ao buscar vagas disponíveis', error);
  } finally {
    loadingVagasDisponiveis.value = false;
  }
};

const selectVaga = (vaga) => {
  newRequisicao.value.vagaId = vaga.id;
  ElMessage.info(`Vaga selecionada: Quadra ${vaga.quadra}, Nº ${vaga.numeroSepultura}`);
  activeTab.value = 'criarRequisicao';
};

// Criar Requisição
const newRequisicao = ref({
  nomeFalecido: '',
  cpfFalecido: '',
  dataFalecimento: null,
  vagaId: '',
  dataRequisicao: null,
  horaRequisicao: '',
  reason: '',
});

const requisicaoRules = {
  nomeFalecido: [{ required: true, message: 'Nome do falecido é obrigatório', trigger: 'blur' }],
  cpfFalecido: [{ required: true, message: 'CPF do falecido é obrigatório', trigger: 'blur' }],
  dataFalecimento: [{ required: true, message: 'Data de falecimento é obrigatória', trigger: 'change' }],
  vagaId: [{ required: true, message: 'Selecione uma vaga', trigger: 'change' }],
  dataRequisicao: [{ required: true, message: 'Data da requisição é obrigatória', trigger: 'change' }],
  horaRequisicao: [{ required: true, message: 'Hora da requisição é obrigatória', trigger: 'change' }]
};

const rgFile = ref(null);
const doFile = ref(null);
const rgFileName = ref('Nenhum arquivo selecionado');
const doFileName = ref('Nenhum arquivo selecionado');

const updateFileNames = () => {
  rgFileName.value = rgFile.value ? rgFile.value.name : 'Nenhum arquivo selecionado';
  doFileName.value = doFile.value ? doFile.value.name : 'Nenhum arquivo selecionado';
};

const clearFiles = () => {
  rgFile.value = null;
  doFile.value = null;
  updateFileNames();
  if (rgUploadRef.value) rgUploadRef.value.clearFiles();
  if (doUploadRef.value) doUploadRef.value.clearFiles();
};

const beforeUpload = (file) => {
  const isPDF = file.type === 'application/pdf';
  const isLt5MB = file.size / 1024 / 1024 < 5;
  if (!isPDF) {
    ElMessage.error('Somente arquivos PDF são permitidos.');
  }
  if (!isLt5MB) {
    ElMessage.error('O tamanho do arquivo não pode exceder 5MB.');
  }
  if (!(isPDF && isLt5MB)) {
    if (rgFile.value) {
      rgFile.value = null;
    }
  }
  return isPDF && isLt5MB;
};

const formatCriadoEm = (row, column, cellValue) => {
  if (!cellValue) return 'N/A';
  return new Date(cellValue).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const onRGFileChange = (file, fileListVal) => {
  if (fileListVal && fileListVal.length > 0) {
    rgFile.value = fileListVal[0];
    updateFileNames();
  }
};
const onDOFileChange = (file, fileListVal) => {
  if (fileListVal && fileListVal.length > 0) {
    doFile.value = fileListVal[0];
    updateFileNames();
  }
};

const onRGFileRemove = () => {
  rgFile.value = null;
  updateFileNames();
  if (rgUploadRef.value) {
    rgUploadRef.value.clearFiles();
  }
};
const onDOFileRemove = () => {
  doFile.value = null;
  updateFileNames();
  if (doUploadRef.value) {
    doUploadRef.value.clearFiles();
  }
};

const requisicaoFormRef = ref(null);
const rgUploadRef = ref(null);
const doUploadRef = ref(null);

const formatDateBRA = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const availableTimeSlots = ref([]);
const loadingTimeSlots = ref(false);

const disabledRequisicaoDate = (date) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date.getTime() < today.getTime();
};

const onDateChange = async (value) => {
  if (value && newRequisicao.value.vagaId) {
    const formattedDate = formatDateBRA(value);
    loadingTimeSlots.value = true;
    try {
      const { data } = await apiClient.get('/requisicoes/available-times', {
        params: {
          vacancyId: newRequisicao.value.vagaId,
          date: formattedDate,
        },
      });
      availableTimeSlots.value = data;
    } catch (error) {
      console.error('Erro ao buscar horários disponíveis', error);
    } finally {
      loadingTimeSlots.value = false;
    }
  }
};

const confirmCreateRequisicao = async () => {
  if (
      !newRequisicao.value.nomeFalecido ||
      !newRequisicao.value.vagaId ||
      !newRequisicao.value.dataRequisicao ||
      !newRequisicao.value.horaRequisicao ||
      !rgFile.value ||
      !doFile.value
  ) {
    alert('Dados incompletos, favor verificar');
    return;
  }
  if (!confirm('Confirma a criação da requisição?')) {
    return;
  }
  await createRequisicao();
};

const createRequisicao = async () => {
  if (!requisicaoFormRef.value) return;
  const formData = new FormData();
  formData.append('nomeFalecido', newRequisicao.value.nomeFalecido || '');
  formData.append('cpfFalecido', (newRequisicao.value.cpfFalecido || '').replace(/[.-]/g, ''));
  if (newRequisicao.value.dataFalecimento) {
    formData.append('dataFalecimento', formatDateBRA(newRequisicao.value.dataFalecimento));
  }
  formData.append('vagaId', newRequisicao.value.vagaId);
  if (newRequisicao.value.dataRequisicao) {
    formData.append('dataRequisicao', formatDateBRA(newRequisicao.value.dataRequisicao));
  }
  if (newRequisicao.value.horaRequisicao) {
    formData.append('horaRequisicao', newRequisicao.value.horaRequisicao);
  }
  formData.append('reason', newRequisicao.value.reason || '');
  formData.append('arquivos', rgFile.value.raw);
  formData.append('arquivos', doFile.value.raw);

  try {
    await apiClient.post('/requisicoes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    ElMessage.success('Requisição criada com sucesso!');
    Object.assign(newRequisicao.value, {
      nomeFalecido: '',
      cpfFalecido: '',
      dataFalecimento: null,
      vagaId: '',
      dataRequisicao: null,
      horaRequisicao: '',
      reason: ''
    });
    clearFiles();
    fetchMinhasRequisicoes();
  } catch (error) {
    clearFiles();
  }
};

const minhasRequisicoes = ref([]);
const loadingMinhasRequisicoes = ref(false);
const fetchMinhasRequisicoes = async () => {
  loadingMinhasRequisicoes.value = true;
  try {
    const { data } = await apiClient.get('/requisicoes/funeraria');
    minhasRequisicoes.value = data;
  } catch (error) {
    console.error('Erro ao buscar requisições', error);
  } finally {
    loadingMinhasRequisicoes.value = false;
  }
};

const formatVaga = (row) => {
  if (row.vaga && row.vaga.quadra && row.vaga.numeroSepultura && row.vaga.tipo) {
    return `Quadra ${row.vaga.quadra} - Sep. ${row.vaga.numeroSepultura} - Tipo ${row.vaga.tipo}`;
  }
  return 'N/A';
};

const requisicoesAndamento = ref([]);
const loadingRequisicoesAndamento = ref(false);
const fetchRequisicoesAndamento = async () => {
  loadingRequisicoesAndamento.value = true;
  try {
    const { data } = await apiClient.get('/requisicoes/andamento');
    requisicoesAndamento.value = data;
  } catch (error) {
    console.error('Erro ao buscar requisições em andamento', error);
  } finally {
    loadingRequisicoesAndamento.value = false;
  }
};

const formatStatus = (row) => {
  return row.status === 'CANCELADO'
      ? 'Rejeitada, entrar em contato.'
      : row.status;
};

const applyCpfMask = () => {
  let digits = newRequisicao.value.cpfFalecido.replace(/\D/g, '');
  if (digits.length > 11) {
    digits = digits.substring(0, 11);
  }
  let masked = '';
  if (digits.length > 0) {
    masked = digits.substring(0, 3);
  }
  if (digits.length >= 4) {
    masked += '.' + digits.substring(3, 6);
  }
  if (digits.length >= 7) {
    masked += '.' + digits.substring(6, 9);
  }
  if (digits.length >= 10) {
    masked += '-' + digits.substring(9, 11);
  }
  newRequisicao.value.cpfFalecido = masked;
};

let vagasInterval;
let minhasInterval;
let andamentoInterval;

watch(activeTab, (tab) => {
  if (tab === 'vagasDisponiveis') fetchVagasDisponiveis();
  if (tab === 'minhasRequisicoes') fetchMinhasRequisicoes();
  if (tab === 'requisicoesAndamento') fetchRequisicoesAndamento();
});

onMounted(() => {
  if (activeTab.value === 'vagasDisponiveis') fetchVagasDisponiveis();
  if (activeTab.value === 'minhasRequisicoes') fetchMinhasRequisicoes();
  if (activeTab.value === 'requisicoesAndamento') fetchRequisicoesAndamento();

  // Auto-refresh disabled for serverless compatibility
  // vagasInterval = setInterval(() => {
  //   if (activeTab.value === 'vagasDisponiveis') fetchVagasDisponiveis();
  // }, 45000);
  // minhasInterval = setInterval(() => {
  //   if (activeTab.value === 'minhasRequisicoes') fetchMinhasRequisicoes();
  // }, 45000);
  // andamentoInterval = setInterval(() => {
  //   if (activeTab.value === 'requisicoesAndamento') fetchRequisicoesAndamento();
  // }, 45000);
});

onUnmounted(() => {
  clearInterval(vagasInterval);
  clearInterval(minhasInterval);
  clearInterval(andamentoInterval);
});
</script>

<style scoped>
.funeraria-dashboard {
  padding: 12px 24px;
  box-sizing: border-box;
  width: 100%;
  max-width: none;
}
.tab-content {
  margin-top: 20px;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.upload-container {
  display: flex;
  align-items: center;
  gap: 20px;
}
.file-status {
  font-size: 14px;
  color: #555;
}
</style>
